<script setup lang="ts">
import { PropType, reactive, nextTick } from 'vue';
import Icon from '@/components/Icon.vue';

export type NavItem = {
    name: string;
    link?: string;
    target?: string;
    rel?: string;
    children?: NavItem[];
};

const props = defineProps({
    items: { type: Array as PropType<NavItem[]>, required: true },
    depth: { type: Number, default: 0 },
    path: { type: String, default: '' },
    mobile: { type: Boolean, default: false },
});

/** Track open submenu paths for mobile accordions (e.g., "0", "0-2", "1-0-3") */
const mobileOpen: Record<string, boolean> = reactive({});

function nodePath(i: number) {
    return props.path ? `${props.path}-${i}` : String(i);
}

function toggle(path: string) {
    mobileOpen[path] = !mobileOpen[path];
    nextTick(() => {
        if (mobileOpen[path] && document !== undefined) {
            const el = document.querySelector<HTMLElement>(
                `[data-path="${path}"] .submenu a, [data-path="${path}"] .submenu button`
            );
            el?.focus?.();
        }
    });
}
</script>

<template>
    <ul role="menu" :aria-label="depth === 0 ? 'Main navigation' : 'Submenu'" :class="[
        // base reset
        'list-none m-0 p-0',
        // root vs submenu layout
        depth === 0
            ? (mobile
                ? 'flex flex-col gap-1'
                : 'flex items-center gap-8')
            : (mobile
                ? 'flex flex-col gap-0.5 p-1 pl-3'
                : [
                    // desktop flyout panel
                    'submenu absolute left-0 top-[calc(100%+0.5rem)] min-w-[14rem] p-2',
                    'bg-zinc-900/95 border border-white/10 rounded',
                    'shadow-[0_14px_28px_rgba(0,0,0,.28),0_2px_4px_rgba(0,0,0,.18)]',
                    'backdrop-blur-[8px] flex flex-col gap-0.5 z-50',
                    // hidden by default; shown on parent :hover/:focus-within
                    'opacity-0 -translate-y-1.5 invisible pointer-events-none',
                    'transition duration-150 ease-out'
                ]
            )
    ]">
        <li v-for="(item, i) in items" :key="i" role="none" :data-path="nodePath(i)" :class="[
            'relative',
            // add hover bridge only for desktop + has children
            (item.children && item.children.length && !mobile)
                ? 'group after:content-[\'\'] after:absolute after:left-0 after:right-0 after:top-full after:h-[10px]'
                : ''
        ]">
            <!-- LEAF -->
            <a v-if="!item.children || item.children.length === 0"
                class="text-white text-[1.0625rem] font-semibold cursor-pointer no-underline bg-transparent border-0 py-1.5 px-1 leading-[1.2] inline-flex items-center gap-1.5 hover:underline focus:outline-none"
                :href="item.link || '#'" role="menuitem" :tabindex="0" :target="item.target || undefined"
                :rel="item.rel || undefined">
                {{ item.name }}
            </a>

            <!-- NODE W/ CHILDREN -->
            <div v-else class="menu-node">
                <!-- Desktop: parent link (hover/focus opens submenu) -->
                <a v-if="!mobile"
                    class="text-white text-[1.0625rem] font-semibold cursor-pointer no-underline bg-transparent border-0 py-1.5 px-1 leading-[1.2] inline-flex items-center gap-1.5 focus:outline-none hover:underline"
                    :href="item.link || '#'" role="menuitem" :target="item.target || undefined"
                    :rel="item.rel || undefined">
                    <span>{{ item.name }}</span>
                    <Icon name="arrow-down" class="text-base opacity-90" aria-hidden="true" />
                </a>

                <!-- Mobile: button toggles accordion -->
                <button v-else type="button" role="menuitem" :aria-haspopup="true"
                    :aria-expanded="!!mobileOpen[nodePath(i)]" @click="toggle(nodePath(i))"
                    class="text-white text-[1.0625rem] font-semibold cursor-pointer no-underline bg-transparent border-0 py-2 px-1 leading-[1.2] inline-flex items-center gap-1.5 w-full text-left focus:outline-none">
                    <span>{{ item.name }}</span>
                    <Icon :name="mobileOpen[nodePath(i)] ? 'arrow-up' : 'arrow-down'" class="text-base opacity-90" />
                </button>

                <!-- Submenu -->
                <MenuList v-if="mobile ? mobileOpen[nodePath(i)] : true" :items="item.children!" :depth="depth + 1"
                    :path="nodePath(i)" :mobile="mobile" :class="[
                        // desktop: reveal on hover/focus of the parent <li>
                        !mobile
                            ? 'group-hover:opacity-100 group-hover:translate-y-0 group-hover:visible group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:translate-y-0 group-focus-within:visible group-focus-within:pointer-events-auto delay-0'
                            : ''
                    ]" />
            </div>
        </li>
    </ul>
</template>
